import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useAnalyticsStore } from '../stores/analyticsStore'
import CostChart from '../components/charts/CostChart'
import UsageChart from '../components/charts/UsageChart'
import FeatureChart from '../components/charts/FeatureChart'

type Period = 'daily' | 'weekly' | 'monthly'

export default function AdminDashboard() {
  const { user } = useAuthStore()

  if (!user?.is_staff) {
    return <Navigate to="/research" replace />
  }

  return <AdminDashboardContent />
}

function AdminDashboardContent() {
  const {
    overview,
    costs,
    usage,
    features,
    sessions,
    loading,
    error,
    fetchOverview,
    fetchCosts,
    fetchUsage,
    fetchFeatures,
    fetchSessions,
  } = useAnalyticsStore()

  const [costPeriod, setCostPeriod] = useState<Period>('daily')
  const [usagePeriod, setUsagePeriod] = useState<Period>('daily')

  useEffect(() => {
    fetchOverview()
    fetchFeatures()
    fetchSessions()
  }, [fetchOverview, fetchFeatures, fetchSessions])

  useEffect(() => {
    fetchCosts(costPeriod)
  }, [costPeriod, fetchCosts])

  useEffect(() => {
    fetchUsage(usagePeriod)
  }, [usagePeriod, fetchUsage])

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-4 pb-2 shrink-0">
        <h1 className="text-xl font-bold">Admin Dashboard</h1>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4 space-y-6">

      {/* Overview Cards */}
      {loading && !overview ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error && !overview ? (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <p className="text-error text-sm mb-4">{error}</p>
          <button
            onClick={() => { fetchOverview(); fetchFeatures(); fetchSessions(); fetchCosts(costPeriod); fetchUsage(usagePeriod) }}
            className="px-4 py-2 rounded-lg bg-elevated text-text-secondary text-sm font-medium hover:bg-bg transition-colors"
          >
            Try again
          </button>
        </div>
      ) : overview ? (
        <div className="grid grid-cols-2 gap-3">
          <StatCard label="Total Users" value={overview.total_users} />
          <StatCard label="Total Threads" value={overview.total_threads} />
          <StatCard label="Total Exchanges" value={overview.total_exchanges} />
          <StatCard label="Cost This Month" value={`$${overview.total_cost.toFixed(2)}`} />
        </div>
      ) : null}

      {/* Active Sessions */}
      {overview && (
        <div className="bg-surface rounded-lg p-4">
          <p className="text-text-secondary text-sm">Active Sessions Today</p>
          <p className="text-2xl font-bold mt-1">{overview.active_sessions_today}</p>
        </div>
      )}

      {/* Cost Chart */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider">AI Costs</h2>
          <PeriodSelector value={costPeriod} onChange={setCostPeriod} />
        </div>
        <div className="bg-surface rounded-lg p-4">
          <CostChart data={costs} />
        </div>
      </section>

      {/* Usage Chart */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider">Usage</h2>
          <PeriodSelector value={usagePeriod} onChange={setUsagePeriod} />
        </div>
        <div className="bg-surface rounded-lg p-4">
          <UsageChart data={usage} />
        </div>
      </section>

      {/* Feature Adoption */}
      <section>
        <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">Feature Adoption</h2>
        <div className="bg-surface rounded-lg p-4">
          <FeatureChart data={features} />
        </div>
      </section>

      {/* Session Analytics */}
      {sessions && (
        <section>
          <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">Sessions</h2>
          <div className="bg-surface rounded-lg p-4 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-text-secondary text-sm">Total Sessions</p>
                <p className="text-lg font-bold">{sessions.total_sessions}</p>
              </div>
              <div>
                <p className="text-text-secondary text-sm">Avg Duration</p>
                <p className="text-lg font-bold">{formatDuration(sessions.avg_duration)}</p>
              </div>
            </div>
            <div>
              <p className="text-text-secondary text-sm mb-2">Device Breakdown</p>
              <div className="flex gap-3">
                <DeviceBadge label="Phone" count={sessions.device_breakdown.phone} />
                <DeviceBadge label="Tablet" count={sessions.device_breakdown.tablet} />
                <DeviceBadge label="Desktop" count={sessions.device_breakdown.desktop} />
              </div>
            </div>
          </div>
        </section>
      )}
      </div>
    </div>
  )
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-surface rounded-lg p-4">
      <p className="text-text-secondary text-sm">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  )
}

function PeriodSelector({ value, onChange }: { value: Period; onChange: (p: Period) => void }) {
  const options: Period[] = ['daily', 'weekly', 'monthly']
  return (
    <div className="flex gap-1 bg-elevated rounded-lg p-0.5">
      {options.map((opt) => (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          className={`px-3 py-1 text-xs rounded-md transition-colors ${
            value === opt
              ? 'bg-accent text-bg font-medium'
              : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          {opt.charAt(0).toUpperCase() + opt.slice(1)}
        </button>
      ))}
    </div>
  )
}

function DeviceBadge({ label, count }: { label: string; count: number }) {
  return (
    <div className="bg-elevated rounded-lg px-3 py-2 flex-1 text-center">
      <p className="text-text-secondary text-xs">{label}</p>
      <p className="text-lg font-bold">{count}</p>
    </div>
  )
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  if (mins < 60) return `${mins}m ${secs}s`
  const hrs = Math.floor(mins / 60)
  const remainMins = mins % 60
  return `${hrs}h ${remainMins}m`
}
